import { memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from '@/i18n/config';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  type?: string;
  schemaData?: any | any[]; // Support single schema or array of schemas
}

export const SEO = memo(({
  title, 
  description, 
  keywords, 
  image, 
  type = 'website', 
  schemaData 
}: SEOProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  
  const siteUrl = 'https://klarfinans.se';
  
  // Canonical URL - startsidan ska alltid peka till rotsidan
  const getCanonicalUrl = () => {
    if (location.pathname === '/sv-SE' || location.pathname === '/') {
      return siteUrl;
    }
    return `${siteUrl}${location.pathname}`;
  };
  
  const canonicalUrl = getCanonicalUrl();
  
  const defaultTitle = 'Klarfinans - Jämför lån, bolån & företagslån';
  const defaultDescription = 'Hitta och jämför de bästa privatlånen, bolånen och företagslånen i Sverige. Enkelt, tryggt och helt gratis.';
  // Real, hosted social image (same one index.html uses). The previous default
  // pointed at /og-image.png, which does not exist — every share/AI preview that
  // didn't pass its own image got a broken image.
  const defaultImage =
    'https://storage.googleapis.com/gpt-engineer-file-uploads/AAuz1TQ6B8Rrr7IN3zAlftxl6VD2/social-images/social-1762723090611-ny klarfinans.png';

  const finalTitle = title ? `${title} | Klarfinans` : defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalImage = image || defaultImage;

  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {keywords && <meta name="keywords" content={keywords.join(', ')} />}
      {/* Let Google + AI answer engines show rich snippets and large image previews. */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />

      {/* Open Graph */}
      <meta property="og:site_name" content="Klarfinans" />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:alt" content={finalTitle} />
      <meta property="og:locale" content="sv_SE" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Schema.org JSON-LD */}
      {schemaData && (
        Array.isArray(schemaData) ? (
          schemaData.map((schema, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(schema)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
        )
      )}
    </Helmet>
  );
});
